import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronUp, Copy, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useStudioStore } from '../../stores/studioStore';
import { useCanvasStore } from '../../stores/canvasStore';
import { useFontStore } from '../../stores/fontStore';
import type { CanvasElement, ButtonElement, TextElement } from '../../types';

type CheckStatus = 'pending' | 'pass' | 'fail' | 'info';

interface CheckResult {
  id: string;
  label: string;
  status: CheckStatus;
  detail: string;
  rawData?: Record<string, unknown>;
}

interface CapturedWarn {
  type: 'variant-reconcile' | 'geometry-migration' | 'other';
  message: string;
  timestamp: number;
}

// Capture console.warn calls related to our migration/reconciliation logic.
// Installs a listener at mount and removes it at unmount.
// Does NOT suppress original warns — they still fire normally.
function useConsoleWarnCapture(): CapturedWarn[] {
  const [warns, setWarns] = useState<CapturedWarn[]>([]);

  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      originalWarn.apply(console, args);

      const message = args
        .map((a) => {
          if (typeof a === 'string') return a;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(' ');

      let type: CapturedWarn['type'] = 'other';
      if (message.includes('[Variant Reconcile]')) type = 'variant-reconcile';
      else if (message.includes('[Geometry Migration]')) type = 'geometry-migration';
      else return;

      setWarns((prev) => [...prev, { type, message, timestamp: Date.now() }]);
    };

    return () => {
      console.warn = originalWarn;
    };
  }, []);

  return warns;
}

export function V1VerificationPanel() {
  const [collapsed, setCollapsed] = useState(false);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const capturedWarns = useConsoleWarnCapture();

  const canvasState = useStudioStore((s) => s.canvasState);
  const activeProject = useStudioStore((s) => s.activeProject);
  const fabricCanvas = useCanvasStore((s) => s.fabricCanvas);
  const fonts = useFontStore((s) => s.fonts);

  const checks: CheckResult[] = useMemo(() => {
    const results: CheckResult[] = [];

    // ===== Check 3.1: Geometry migration ran for buttons =====
    // Verifies that Phase F.2 button geometry migration (Edit 4 of fix pass) ran on load.
    // After migration, buttons should have _geometryVersion >= 1 stamped.
    // Captured [Geometry Migration] warns are proof the migration block executed.
    const buttons = canvasState.elements.filter(
      (el): el is ButtonElement => el.type === 'button'
    );
    const migratedButtons = buttons.filter(
      (b) => typeof (b as any)._geometryVersion === 'number' && (b as any)._geometryVersion >= 1
    );
    const unmigratedButtons = buttons.filter(
      (b) => typeof (b as any)._geometryVersion !== 'number' || (b as any)._geometryVersion < 1
    );
    const geometryMigrationWarns = capturedWarns.filter(
      (w) => w.type === 'geometry-migration'
    );

    let geomStatus: CheckStatus = 'pending';
    let geomDetail = '';
    if (buttons.length === 0) {
      geomStatus = 'info';
      geomDetail = 'No buttons in active project. Open Miami Social Ambassador or New Arrivals.';
    } else if (migratedButtons.length === buttons.length) {
      geomStatus = 'pass';
      geomDetail = `All ${buttons.length} buttons migrated (_geometryVersion >= 1 stamped). Captured warns: ${geometryMigrationWarns.length}.`;
    } else if (unmigratedButtons.length > 0 && geometryMigrationWarns.length > 0) {
      geomStatus = 'pass';
      geomDetail = `Migration ran (captured ${geometryMigrationWarns.length} warns). ${migratedButtons.length} of ${buttons.length} buttons stamped.`;
    } else {
      geomStatus = 'fail';
      geomDetail = `${unmigratedButtons.length} buttons not yet migrated AND no migration warns captured. Reload project to trigger migration.`;
    }

    results.push({
      id: '3.1',
      label: 'Geometry migration ran on button load',
      status: geomStatus,
      detail: geomDetail,
      rawData: {
        buttonCount: buttons.length,
        migratedCount: migratedButtons.length,
        unmigratedCount: unmigratedButtons.length,
        capturedWarnCount: geometryMigrationWarns.length,
        warns: geometryMigrationWarns.map((w) => w.message),
        buttons: buttons.map((b) => ({
          id: b.id,
          name: b.name,
          x: b.x,
          y: b.y,
          width: b.width,
          height: b.height,
          _geometryVersion: (b as any)._geometryVersion ?? null,
        })),
      },
    });

    // ===== Check 3.2: Button Fabric props intact (selectable / evented / hasControls) =====
    // Verifies that Edit 6 of fix pass (button creation reverse translation) did not
    // drop any Fabric properties. Reads sample button's Fabric object and checks
    // the three critical interaction flags.
    // EXPANDED DIAGNOSTIC: captures every Fabric object on the canvas with its flags,
    // so we can see whether the button is rendering under an unexpected flag or path.
    let basePropsStatus: CheckStatus = 'pending';
    let basePropsDetail = '';
    let basePropsRaw: Record<string, unknown> = {};
    if (!fabricCanvas) {
      basePropsStatus = 'pending';
      basePropsDetail = 'Fabric canvas not yet initialized.';
    } else if (buttons.length === 0) {
      basePropsStatus = 'info';
      basePropsDetail = 'No buttons to verify.';
    } else {
      const allObjects = fabricCanvas.getObjects() as any[];
      const buttonFabricObjs = allObjects.filter((o) => o && o._csIsButton === true);

      // Build a full inventory of every Fabric object on canvas with identifying flags.
      const objectInventory = allObjects.map((o, idx) => ({
        index: idx,
        type: o?.type ?? 'unknown',
        constructorName: o?.constructor?.name ?? 'unknown',
        _csIsArtboard: o?._csIsArtboard ?? null,
        _csIsArtboardBorder: o?._csIsArtboardBorder ?? null,
        _csIsGrid: o?._csIsGrid ?? null,
        _csIsButton: o?._csIsButton ?? null,
        _csIsSvg: o?._csIsSvg ?? null,
        _csButtonBg: o?._csButtonBg ? 'present' : null,
        _csButtonText: o?._csButtonText ? 'present' : null,
        _csElementId: o?._csElementId ?? null,
        left: o?.left,
        top: o?.top,
        width: o?.width,
        height: o?.height,
        visible: o?.visible,
        selectable: o?.selectable,
        evented: o?.evented,
        opacity: o?.opacity,
        hasInnerObjects: typeof o?.getObjects === 'function' ? o.getObjects().length : null,
      }));

      // Also report what's known about each store button to cross-reference
      const storeButtonInfo = buttons.map((b) => ({
        id: b.id,
        name: b.name,
        type: b.type,
        x: b.x,
        y: b.y,
        width: b.width,
        height: b.height,
        rotation: b.rotation,
        visible: b.visible,
        locked: b.locked,
        _geometryVersion: (b as any)._geometryVersion ?? null,
      }));

      if (buttonFabricObjs.length === 0) {
        basePropsStatus = 'fail';
        basePropsDetail = `${buttons.length} buttons in store but 0 Fabric button objects on canvas. Render path broken. See raw data for full canvas object inventory.`;
        basePropsRaw = {
          storeButtonCount: buttons.length,
          fabricButtonCount: 0,
          allFabricObjectCount: allObjects.length,
          storeButtons: storeButtonInfo,
          canvasObjectInventory: objectInventory,
        };
      } else {
        const sample = buttonFabricObjs[0];
        const isSelectable = sample.selectable !== false;
        const isEvented = sample.evented !== false;
        const hasControls = sample.hasControls !== false;
        const allOk = isSelectable && isEvented && hasControls;
        basePropsStatus = allOk ? 'pass' : 'fail';
        basePropsDetail = allOk
          ? `Sample button has selectable=${isSelectable}, evented=${isEvented}, hasControls=${hasControls}. Verified ${buttonFabricObjs.length} button object(s) on canvas.`
          : `Sample button missing properties: selectable=${isSelectable}, evented=${isEvented}, hasControls=${hasControls}.`;
        basePropsRaw = {
          fabricButtonCount: buttonFabricObjs.length,
          sample: {
            type: sample.type,
            constructorName: sample.constructor?.name,
            left: sample.left,
            top: sample.top,
            width: sample.width,
            height: sample.height,
            angle: sample.angle,
            originX: sample.originX,
            originY: sample.originY,
            selectable: isSelectable,
            evented: isEvented,
            hasControls,
            opacity: sample.opacity,
            visible: sample.visible,
            _csIsButton: sample._csIsButton,
            _csElementId: sample._csElementId,
          },
          storeButtons: storeButtonInfo,
          canvasObjectInventory: objectInventory,
        };
      }
    }

    results.push({
      id: '3.2',
      label: 'Button properties intact after Edit 6 (selectable / evented / hasControls)',
      status: basePropsStatus,
      detail: basePropsDetail,
      rawData: basePropsRaw,
    });

    // ===== Check 3.3: _geometryVersion stamped on all buttons (persistence readiness) =====
    // Reports current persistence-ready state. True verification requires user to
    // save then reload — this check confirms all buttons currently carry the stamp.
    let persistStatus: CheckStatus = 'info';
    let persistDetail = 'Save project then reload to verify _geometryVersion persists.';
    if (buttons.length > 0 && migratedButtons.length === buttons.length) {
      persistStatus = 'pass';
      persistDetail = `All ${buttons.length} buttons currently stamped. After save + reload, this check should remain PASS with zero new geometry-migration warns.`;
    } else if (buttons.length > 0) {
      persistStatus = 'fail';
      persistDetail = `${unmigratedButtons.length} of ${buttons.length} buttons missing _geometryVersion stamp.`;
    }

    results.push({
      id: '3.3',
      label: '_geometryVersion stamped on all buttons (persistence readiness)',
      status: persistStatus,
      detail: persistDetail,
    });

    // ===== Check 3.4: Manual-intent rotated text — widthIntent preserved =====
    // Verifies that Edits 1 + 2 of fix pass (advance-width + height self-heal gates)
    // are correctly preserving widthIntent='manual' on rotated text elements.
    const rotatedManualText = canvasState.elements.filter(
      (el): el is TextElement =>
        el.type === 'text' &&
        (el as TextElement).widthIntent === 'manual' &&
        typeof el.rotation === 'number' &&
        el.rotation !== 0
    );

    let manualTextStatus: CheckStatus = 'pending';
    let manualTextDetail = '';
    if (rotatedManualText.length === 0) {
      manualTextStatus = 'info';
      manualTextDetail = 'No rotated manual-intent text in active project. Open New Arrivals.';
    } else {
      const allManual = rotatedManualText.every((el) => el.widthIntent === 'manual');
      manualTextStatus = allManual ? 'pass' : 'fail';
      manualTextDetail = allManual
        ? `${rotatedManualText.length} rotated text element(s) have widthIntent='manual' preserved. Width values shown in raw data.`
        : `widthIntent lost on at least one rotated text element.`;
    }

    results.push({
      id: '3.4',
      label: 'Manual-intent rotated text preserved (widthIntent intact)',
      status: manualTextStatus,
      detail: manualTextDetail,
      rawData: {
        rotatedManualTextCount: rotatedManualText.length,
        elements: rotatedManualText.map((el) => ({
          id: el.id,
          name: el.name,
          text: el.text,
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
          rotation: el.rotation,
          widthIntent: el.widthIntent,
          fontSize: (el as any).fontSize,
          fontFamily: (el as any).fontFamily,
        })),
      },
    });

    // ===== Check 3.5: Export pipeline emits transform-origin (manual verification) =====
    // Cannot run an export from the panel. Flags this as requiring user action.
    results.push({
      id: '3.5',
      label: 'Rotated text export includes transform-origin: 0 0 (manual)',
      status: 'info',
      detail:
        'Run an Export \u2192 JPEG + .txt on New Arrivals. Open the .txt file. For each rotated NEW and ARRIVALS element, verify the CSS contains BOTH "transform: rotate(...)" AND "transform-origin: 0 0". Report PASS or FAIL when pasting diagnostic.',
    });

    // ===== Check 3.6: Visual export parity (manual verification) =====
    results.push({
      id: '3.6',
      label: 'Visual export parity (canvas vs exported JPEG) (manual)',
      status: 'info',
      detail:
        'Run an Export \u2192 JPEG. Open the .jpeg side-by-side with the canvas. Verify rotated text is NOT displaced 7-35px and buttons are NOT displaced ~width/2. Report PASS or FAIL when pasting diagnostic.',
    });

    // ===== Check 3.7: Variant/weight reconciliation =====
    // Verifies Edit 3 of fix pass. Walks every element with a fontVariantId and
    // checks whether the stored fontWeight/fontStyle agree with the variant in cs_fonts.
    // After reconciliation, mismatches should be zero (if any existed at load,
    // they should have been auto-corrected and captured as warns).
    const variantReconcileWarns = capturedWarns.filter(
      (w) => w.type === 'variant-reconcile'
    );
    const elementsWithVariantId = canvasState.elements.filter(
      (el) => typeof (el as any).fontVariantId === 'string' && (el as any).fontVariantId.length > 0
    );

    const mismatches: Array<{
      id: string;
      name: string | undefined;
      storedWeight: unknown;
      storedStyle: unknown;
      variantId: string;
      variantInFonts: boolean;
      variantWeight: string | null;
      variantStyle: string | null;
    }> = [];

    for (const el of elementsWithVariantId) {
      const variantId = (el as any).fontVariantId as string;
      const variant = fonts.find((f) => f.id === variantId);
      if (variant) {
        const variantWeightNum = parseInt(variant.fontWeight, 10);
        const storedWeight = (el as any).fontWeight;
        const variantStyle = variant.fontStyle === 'italic' ? 'italic' : 'normal';
        const storedStyle = (el as any).fontStyle;
        const weightMismatch =
          Number.isFinite(variantWeightNum) && variantWeightNum !== Number(storedWeight);
        const styleMismatch = variantStyle !== storedStyle;
        if (weightMismatch || styleMismatch) {
          mismatches.push({
            id: el.id,
            name: (el as any).name,
            storedWeight,
            storedStyle,
            variantId,
            variantInFonts: true,
            variantWeight: variant.fontWeight,
            variantStyle: variant.fontStyle,
          });
        }
      } else {
        mismatches.push({
          id: el.id,
          name: (el as any).name,
          storedWeight: (el as any).fontWeight,
          storedStyle: (el as any).fontStyle,
          variantId,
          variantInFonts: false,
          variantWeight: null,
          variantStyle: null,
        });
      }
    }

    let variantStatus: CheckStatus = 'pending';
    let variantDetail = '';
    if (elementsWithVariantId.length === 0) {
      variantStatus = 'info';
      variantDetail = 'No elements with fontVariantId in active project.';
    } else if (mismatches.length === 0 && variantReconcileWarns.length === 0) {
      variantStatus = 'pass';
      variantDetail = `All ${elementsWithVariantId.length} variant references are consistent. No reconciliation needed.`;
    } else if (mismatches.length === 0 && variantReconcileWarns.length > 0) {
      variantStatus = 'pass';
      variantDetail = `Reconciliation ran (${variantReconcileWarns.length} warns captured). All variants now consistent.`;
    } else {
      variantStatus = 'fail';
      variantDetail = `${mismatches.length} elements still have variant/weight mismatch. Reconciliation may not have run, or PropertyPanel desync.`;
    }

    results.push({
      id: '3.7',
      label: 'Variant/weight reconciliation (Edit 3 of fix pass)',
      status: variantStatus,
      detail: variantDetail,
      rawData: {
        elementsWithVariantId: elementsWithVariantId.length,
        mismatchCount: mismatches.length,
        capturedReconcileWarns: variantReconcileWarns.length,
        warns: variantReconcileWarns.map((w) => w.message),
        mismatches,
      },
    });

    return results;
  }, [canvasState.elements, fabricCanvas, fonts, capturedWarns]);

  const passCount = checks.filter((c) => c.status === 'pass').length;
  const failCount = checks.filter((c) => c.status === 'fail').length;
  const pendingCount = checks.filter(
    (c) => c.status === 'pending' || c.status === 'info'
  ).length;

  const handleCopy = useCallback(async () => {
    const payload = {
      timestamp: new Date().toISOString(),
      activeProjectId: activeProject?.id ?? null,
      activeProjectName: activeProject?.name ?? null,
      canvasDimensions: canvasState.dimensions,
      elementCount: canvasState.elements.length,
      summary: {
        pass: passCount,
        fail: failCount,
        pending: pendingCount,
        total: checks.length,
      },
      checks: checks.map((c) => ({
        id: c.id,
        label: c.label,
        status: c.status,
        detail: c.detail,
        rawData: c.rawData,
      })),
      capturedWarns,
    };
    const text = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAt(Date.now());
    } catch (e) {
      console.error('[V1 Panel] Clipboard write failed', e);
    }
  }, [
    checks,
    capturedWarns,
    activeProject,
    canvasState,
    passCount,
    failCount,
    pendingCount,
  ]);

  return (
    <div className="w-[420px] rounded-lg border border-white/10 bg-[#1a1a1a] shadow-2xl">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-white">V1 Verification Panel</span>
          <span className="text-xs text-white/50 truncate">
            ({passCount} pass / {failCount} fail / {pendingCount} pending)
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="rounded p-1 text-white/60 hover:bg-white/10 hover:text-white"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="max-h-[480px] overflow-y-auto sidebar-scroll px-3 py-2">
            {checks.map((check) => (
              <div
                key={check.id}
                className="mb-2 rounded border border-white/5 bg-white/[0.02] p-2"
              >
                <div className="flex items-start gap-2">
                  <StatusIcon status={check.status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white">
                      Test {check.id}: {check.label}
                    </div>
                    <div className="mt-1 text-xs text-white/60">{check.detail}</div>
                    {check.rawData && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[10px] text-white/40 hover:text-white/60">
                          Show raw data
                        </summary>
                        <pre className="mt-1 overflow-x-auto rounded bg-black/30 p-2 text-[10px] text-white/70">
{JSON.stringify(check.rawData, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-white/10 px-4 py-3">
            <div className="text-xs text-white/50 truncate flex-1 mr-2">
              {activeProject?.name ?? 'No project loaded'}
            </div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 rounded bg-cyan-500/20 px-3 py-1.5 text-xs font-medium text-cyan-400 hover:bg-cyan-500/30 flex-shrink-0"
            >
              <Copy size={12} />
              {copiedAt && Date.now() - copiedAt < 2000 ? 'Copied!' : 'Copy Diagnostic'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: CheckStatus }) {
  if (status === 'pass') {
    return <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0 text-emerald-400" />;
  }
  if (status === 'fail') {
    return <XCircle size={14} className="mt-0.5 flex-shrink-0 text-rose-400" />;
  }
  if (status === 'info') {
    return <Clock size={14} className="mt-0.5 flex-shrink-0 text-cyan-400" />;
  }
  return <Clock size={14} className="mt-0.5 flex-shrink-0 text-white/40" />;
}
